import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/our-story", label: "Our Story" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Wordmark = ({ className = "", light = false }) => (
  <Link to="/" data-testid="brand-wordmark" className={`flex items-baseline gap-1 ${className}`}>
    <span className={`font-fraunces text-2xl font-semibold tracking-tight ${light ? "text-cream" : "text-charcoal"}`}>
      Kindred
    </span>
    <span className="font-fraunces text-2xl italic font-light text-rust">Mile</span>
  </Link>
);

export default function Navbar() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [pathname]);

  const onHome = pathname === "/";
  const solid = scrolled || !onHome || mobile;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 z-50 w-full transition-colors duration-500 ${
        solid ? "bg-cream/90 backdrop-blur-md border-b border-charcoal/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <Wordmark light={onHome && !solid} />

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `nav-underline font-manrope text-sm tracking-wide ${
                  onHome && !solid ? "text-cream" : "text-charcoal"
                } ${isActive ? "is-active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="open-cart-button"
            onClick={() => setOpen(true)}
            className={`group relative flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
              onHome && !solid ? "text-cream hover:bg-cream/10" : "text-charcoal hover:bg-charcoal/5"
            }`}
          >
            <ShoppingBag className="h-5 w-5 transition-transform duration-300 ease-out group-hover:scale-110 group-active:scale-95" strokeWidth={1.6} />
            <span className="hidden sm:inline font-manrope">Cart</span>
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rust text-[11px] font-semibold text-cream"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((m) => !m)}
            className={`md:hidden ${onHome && !solid ? "text-cream" : "text-charcoal"}`}
          >
            {mobile ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {mobile && (
        <div className="border-t border-charcoal/10 bg-cream px-5 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="font-fraunces text-2xl text-charcoal"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
